'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { Button } from '@/components/ui';

// Root-level error boundary. This sits OUTSIDE of `app/[locale]/`, so
// next-intl's `useTranslations` is not available here. Just like
// `app/not-found.tsx`, we render the message in all six supported
// languages in parallel so every visitor sees something they can read
// without picking an arbitrary fallback.
const ERROR_COPIES: Array<{ lang: string; title: string; retry: string }> = [
  { lang: 'en', title: 'Something went wrong', retry: 'Try again' },
  { lang: 'de', title: 'Etwas ist schiefgelaufen', retry: 'Erneut versuchen' },
  { lang: 'es', title: 'Algo salió mal', retry: 'Reintentar' },
  { lang: 'fr', title: 'Une erreur est survenue', retry: 'Réessayer' },
  { lang: 'it', title: 'Qualcosa è andato storto', retry: 'Riprova' },
  { lang: 'ja', title: 'エラーが発生しました', retry: '再試行' },
];

export default function Error({ error, reset }: { error: Error; reset: () => void }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-zen-app px-6 py-12 text-zen-text">
      {error?.message ? <p className="max-w-lg text-center text-sm text-zen-text-muted">{error.message}</p> : null}
      <ul className="flex flex-col items-center gap-6 text-center">
        {ERROR_COPIES.map((copy) => (
          <li key={copy.lang} lang={copy.lang} className="flex flex-col items-center gap-2">
            <h1 className="text-lg font-semibold">{copy.title}</h1>
            <Button type="button" variant="outline" className="rounded-full" onClick={reset}>
              {copy.retry}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
